/**
 * Client-side spatial scoping for AI Layers selections (clicked polygon or
 * drawn area). Geometry is used only to filter features; snapshots stay
 * attribute-only.
 */

import { booleanDisjoint, booleanPointInPolygon } from '@turf/turf';
import type { Feature, Geometry, MultiPolygon, Polygon } from 'geojson';
import { isAreaGeometry } from 'components/IAP/utils/actionSpatialFilter';

export type AiSpatialFilter = Polygon | MultiPolygon;

export { isAreaGeometry };

function asFeature(geometry: Geometry): Feature {
  return { type: 'Feature', properties: {}, geometry };
}

/** Whether a GeoJSON geometry overlaps the user's map selection. */
export function geometryIntersectsArea(
  geometry: Geometry | null | undefined,
  area: AiSpatialFilter
): boolean {
  if (!geometry) return false;

  if (geometry.type === 'Point') {
    try {
      return booleanPointInPolygon(geometry.coordinates, area);
    } catch {
      return false;
    }
  }

  try {
    return !booleanDisjoint(asFeature(geometry), asFeature(area));
  } catch {
    return false;
  }
}

export function parseAiSpatialFilter(
  raw: unknown
): AiSpatialFilter | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const rec = raw as { type?: unknown; coordinates?: unknown };
  if (
    (rec.type === 'Polygon' || rec.type === 'MultiPolygon') &&
    Array.isArray(rec.coordinates)
  ) {
    return rec as AiSpatialFilter;
  }
  return undefined;
}

export function getAiMapScopeLabel(
  layers: Array<{
    spatialFilterLabel?: string;
    pickedFeatureName?: string;
    spatialFilter?: unknown;
  }>
): string | null {
  for (const layer of layers) {
    if (layer.spatialFilterLabel?.trim())
      return layer.spatialFilterLabel.trim();
    if (layer.pickedFeatureName?.trim()) {
      return `Selected: ${layer.pickedFeatureName.trim()}`;
    }
  }
  if (layers.some((l) => parseAiSpatialFilter(l.spatialFilter))) {
    return 'Area on map';
  }
  return null;
}
